/**
 * League configuration — the current (post-2023 Competition Committee) state of the league.
 *
 * Every value here is traceable to a rule in the rulebook. The `ruleRef` comments use the
 * league's own citation format: YEAR-SECTION-RULE (e.g. 2019-II-1 = 2019 rulebook,
 * section II "Competitive", Rule 1). Rules passed in 2022 and 2023 were not numbered in the
 * source document, so they are cited by title.
 *
 * When the Competition Committee changes something, change it HERE and nowhere else.
 * The engine derives everything from this object.
 */

export interface LeagueConfig {
  /** Number of GMs. */
  teams: number;
  /** Rounds in the main fantasy draft (== roster size). */
  draftRounds: number;
  /** Total roster spots, excluding IR. */
  rosterSize: number;
  /** Injured reserve slots that do not count against rosterSize. */
  irSlots: number;
  /** Keepers a GM may declare with no luxury tax. Farm graduates count inside this number. */
  freeKeepers: number;
  /** Absolute maximum keepers a GM may declare, tax included. */
  maxKeepers: number;
  /** Luxury tax in FAAB dollars, indexed by TOTAL keepers declared. Cumulative total, not marginal. */
  luxuryTaxByKeeperCount: Readonly<Record<number, number>>;
  /** Base FAAB budget issued to every GM each season. */
  faabBase: number;
  /** Real dollars required to purchase 1 FAAB dollar. */
  faabPurchaseRate: number;
  /** Fine (FAAB) for dropping a drafted player before Week 1. */
  postDraftDropFine: number;
  /** Fine (FAAB) for refusing an "Own Your Soul" team-name change. */
  ownYourSoulFine: number;
  /** Maximum college players a GM may hold in the farm system at once. */
  maxFarmPlayers: number;
  /** Added to an NFL draft round to get the fantasy keeper round for a farm graduate. */
  farmGraduationPenaltyOffset: number;
  /** Weeks in the fantasy regular season. */
  regularSeasonWeeks: number;
  /** Earliest draft round that qualifies for the sleeper reward. */
  sleeperMinRound: number;
  /** Fraction of unspent FAAB that rolls over to next season. */
  faabRolloverRate: number;
}

export const LEAGUE_2026: LeagueConfig = {
  teams: 10,
  draftRounds: 16, // 2017-IV-1 §4 (IDP eliminated, back to 16) + 2019-IV-1 §2
  rosterSize: 16, // 2019-III-1 §3 (kicker removed, third flex added, size unchanged)
  irSlots: 2, // 2022 "Injured Reserve Usage Expansion" §1
  freeKeepers: 4, // 2020-III-1 §2
  maxKeepers: 8, // 2023 "Expand Maximum Number Of Eligible Keepers" §1
  luxuryTaxByKeeperCount: {
    // 2023 "Expand Maximum Number Of Eligible Keepers" §2. Values are cumulative totals.
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 22,
    6: 44,
    7: 66,
    8: 88,
  },
  faabBase: 100, // 2019-I-1 §1 refers to "the standard $100"
  faabPurchaseRate: 2, // 2020-I-6 §2 — $2 real buys $1 FAAB
  postDraftDropFine: 5, // 2019-III-4 §2
  ownYourSoulFine: 50, // 2020-I-5 §5
  /*
   * FOUR SINCE 2025 (Tim, 9 September: "farm rosters were increased from 3 to 4 with
   * 2025-proposal-04-farm-two-rounds"). §10: "GM's cannot, at any point in time, have more than 4
   * players in their farm system — this is an expansion from the current 3-player limit". The
   * three came from 2022 "Farm System Auction Declarations And Acquisitions" §1 and the app had
   * carried it through a season that had already replaced it, so every cap on the screen — the
   * rail's `0 of 3`, the roster panel's `1/3`, `farmBlock`, the keeper arithmetic — was a year out
   * of date at once.
   */
  maxFarmPlayers: 4, // 2025-proposal-04-farm-two-rounds §10
  farmGraduationPenaltyOffset: 10, // 2013-III-1 §4
  regularSeasonWeeks: 14, // 2021-I-1 §1
  sleeperMinRound: 10, // 2017-II-2 §2
  faabRolloverRate: 0.5, // 2018-II-4 §1
};

/**
 * The starting lineup, in the order Yahoo lists it. Read off the league settings page rather
 * than inferred: QB, two WR, two RB, a TE, three flexes and a defense — no kicker slot, which
 * surprises people every year — then six bench and two IR (2019-III-1 §3, 2022 IR expansion).
 */
export const ROSTER_SLOTS: ReadonlyArray<{ slot: string; accepts: readonly string[] }> = [
  { slot: 'QB', accepts: ['QB'] },
  { slot: 'WR', accepts: ['WR'] },
  { slot: 'WR', accepts: ['WR'] },
  { slot: 'RB', accepts: ['RB'] },
  { slot: 'RB', accepts: ['RB'] },
  { slot: 'TE', accepts: ['TE'] },
  { slot: 'W/R/T', accepts: ['WR', 'RB', 'TE'] },
  { slot: 'W/R/T', accepts: ['WR', 'RB', 'TE'] },
  { slot: 'W/R/T', accepts: ['WR', 'RB', 'TE'] },
  { slot: 'DEF', accepts: ['DEF'] },
];

/**
 * The league's Discord.
 *
 * Every declaration, counter-offer and argument this app records started life in there — the
 * 24-hour trade window (2013-II-1) happens in conversation, not in a form — so a link back to it
 * belongs on the one menu that is about the person rather than about the season.
 *
 * A constant rather than a setting because it has not changed in a decade and a commissioner
 * editing it is not a workflow. The trade integration Tim is designing will need something
 * different and server-side: posting to a channel takes a WEBHOOK, which is a secret and must not
 * ship in the bundle where any manager can read it.
 */
export const DISCORD_URL =
  'https://discord.com/channels/1266815821779570799/1269999484084158671';

/**
 * The league on Yahoo.
 *
 * The app is the rulebook, the ledger and the draftboard; Yahoo is still where the roster, the
 * waiver claim and the Sunday score live, and every manager keeps a tab of it open from September.
 * Sitting above Discord on the same menu, the two are the pair of "the places this league also
 * happens" — which is why they are links out rather than anything the app pretends to embed.
 *
 * `f1/338028` is the league id, unchanged since 2011. A constant for the same reason DISCORD_URL
 * is one: it does not change, and a commissioner editing it is not a workflow.
 */
export const LEAGUE_URL = 'https://football.fantasysports.yahoo.com/f1/338028';
